/**
 * ExecutiveManual — /#/manuals/executive
 * Long-scroll, well-typeset HTML manual mirroring the Executive markdown source.
 *
 * Brand locks: Navy #0B0B0F · Red #DC2626 · AIP bg #FAFAF7.
 * No exclamation points. No emojis. Never "rep".
 *
 * W66.X4 — rewritten to match current production:
 *   - /#/sales-plan is now the Top-10 Account Sales Plan workbook (SalesPlanTopTen.tsx).
 *   - Executive dashboard tiles corrected to match ExecutiveView.tsx (Annual Objective,
 *     Booked YTD, Forecast, Gap to Objective, AE Scoreboard, Coverage Composition,
 *     Risk Signals).
 *   - PROBABLE bucket removed. Canonical forecast buckets: COMMIT · UPSIDE · OMITTED · WON.
 *   - Strategic accounts updated to full Top-10 list.
 *   - Nav bar updated to reflect post-W66.D sidebar structure.
 *   - Helix route clarified (/#/helix = territory aggregate; /#/opportunity-dna = per-account).
 *   - Quota Workbook route noted as /#/sales-plan/quota (manager/executive only).
 */

import ManualLayout, {
  ManualSectionHeading,
  Callout,
  ManualImage,
  StepList,
  BulletList,
  ManualPara,
  ManualTable,
  type ManualSection,
} from "@/components/manuals/ManualLayout";

import shot01 from "@/assets/manuals/executive/01-dashboard-executive.png";
import shot02 from "@/assets/manuals/executive/02-exec-revenue-rollup.png";
import shot03 from "@/assets/manuals/executive/03-exec-pipeline-health.png";
import shot04 from "@/assets/manuals/executive/04-exec-team-attainment.png";
import shot05 from "@/assets/manuals/executive/05-exec-strategic-accounts.png";
import shot06 from "@/assets/manuals/executive/06-exec-forecast-vs-plan.png";

const SECTIONS: ManualSection[] = [
  { id: "welcome",    label: "Welcome and how to use this manual" },
  { id: "role",       label: "The role: what an Executive owns in M-PACT" },
  { id: "login",      label: "Logging in and navigating" },
  { id: "dashboard",  label: "Your dashboard: every tile" },
  { id: "pipeline",   label: "Pipeline health: reading the funnel" },
  { id: "strategic",  label: "The strategic-account view: Tier 1 OEM portfolio" },
  { id: "sales-plan", label: "Top-10 Account Sales Plan workbook" },
  { id: "forecast",   label: "Forecast vs plan: building the board narrative" },
  { id: "coaching",   label: "Coaching the coaches: 1-on-1s with your VP and managers" },
  { id: "rhythm",     label: "Monthly and quarterly rhythm" },
  { id: "glossary",   label: "Glossary, gotchas, and closing note" },
];

const BLACK = "#0B0B0F";

export default function ExecutiveManual() {
  return (
    <ManualLayout
      role="Executive"
      title="Attilius Operating Manual — Executive"
      meta="Executive role · v1.1 · June 2026"
      sections={SECTIONS}
    >
      {/* ── Chapter 1 — Welcome ──────────────────────────────────────────────── */}
      <ManualSectionHeading id="welcome" num={1} title="Welcome and how to use this manual" />

      <ManualPara>
        This manual is for the executive who owns the number. Whether your title is CEO, CRO, VP of
        Sales, or Chief Commercial Officer, your obligation inside M-PACT is the same: you carry the
        annual revenue commitment, you defend it in the boardroom, and you use AIP to know whether
        the plan is real before you walk into that room.
      </ManualPara>

      <ManualPara>
        Read Chapter 2 first. Then read Chapter 4 on the dashboard and Chapter 8 on forecast vs
        plan — those two chapters carry the highest value for a first-time executive user. Chapters
        5, 6, 7, and 9 add depth once the basics are anchored.
      </ManualPara>

      <ManualPara>
        This manual covers the executive-role surfaces in AIP:
        <code> /#/dashboards/executive</code> (daily view),
        <code> /#/plan-vs-actual</code> (revenue actuals vs annual commitment),
        <code> /#/pipeline</code> (funnel coverage and stage health),
        <code> /#/leaderboard</code> (per-manager and per-AE attainment),
        <code> /#/accounts</code> (strategic-account portfolio), and
        <code> /#/sales-plan</code> (Top-10 Account Sales Plan workbook).
      </ManualPara>

      {/* ── Chapter 2 — Role ─────────────────────────────────────────────────── */}
      <ManualSectionHeading id="role" num={2} title="The role: what an Executive owns inside M-PACT" />

      <ManualPara>
        The Executive is the final accountability layer in M-PACT. You do not own individual deals.
        You own the architecture of the year. Five things belong to the Executive and to no one else.
      </ManualPara>

      <BulletList
        items={[
          <span><strong style={{ color: BLACK }}>The strategic narrative.</strong> Why does the company win in manufacturing? The narrative must be visible in every Tier 1 account plan, every hook theme, and every manager's coaching direction. When the narrative is absent, the funnel fragments.</span>,
          <span><strong style={{ color: BLACK }}>The annual plan.</strong> The annual revenue commitment — quota by account executive, by quarter, by territory — is managed at <code>/#/sales-plan/quota</code> (Quota Workbook tab, manager and executive only). The primary sales planning surface is <code>/#/sales-plan</code>, which shows the Top-10 Account workbook. You set the annual objective. Managers distribute it. Account executives execute against it. A plan that is wrong in January creates a forecast problem in September.</span>,
          <span><strong style={{ color: BLACK }}>The boardroom number.</strong> WON closed revenue plus COMMIT bucket weighted at 90%. AIP computes this automatically. Your job is to understand the composition of that number before you state it.</span>,
          <span><strong style={{ color: BLACK }}>The win-rate trend.</strong> Win rate is the compounding metric. A win rate that moves from 45% to 55% over four quarters is worth more than a pipeline that grows 20% with a flat win rate. You watch this number the way a CFO watches gross margin.</span>,
          <span><strong style={{ color: BLACK }}>The strategic-account portfolio.</strong> Tier 1 OEM accounts — Stellantis, Toyota, Aptiv, Marelli, Ford, Bosch NA, Bose, Lear, Rivian, BorgWarner — are the proof cases that define the category. These accounts receive direct CEO attention. Not delegation — presence.</span>,
        ]}
      />

      <BulletList
        items={[
          <span><strong style={{ color: BLACK }}>M — Methodology.</strong> You set the standard that all 10 checkpoints must be green before a deal enters COMMIT. You enforce this in every QBR.</span>,
          <span><strong style={{ color: BLACK }}>P — Process / Prioritization.</strong> You own the annual plan cadence — plan published in Q4 of the prior year, reforecast at Q1 close, reviewed at Q2 midpoint.</span>,
          <span><strong style={{ color: BLACK }}>A — Accountability.</strong> You hold managers accountable for their teams. You do not hold individual account executives accountable — that is the manager's job.</span>,
          <span><strong style={{ color: BLACK }}>C — Cadence / Coaching.</strong> Your coaching surface is the manager and the VP. AIP gives you attainment by account executive, pipeline coverage by quarter, and win-rate by segment.</span>,
          <span><strong style={{ color: BLACK }}>T — Timing / Timeline management.</strong> You own the firm timeline at the portfolio level. Which deals need to close this quarter because of a board commitment? Those go to the top of the strategic-account watch list.</span>,
        ]}
      />

      {/* ── Chapter 3 — Login ────────────────────────────────────────────────── */}
      <ManualSectionHeading id="login" num={3} title="Logging in and navigating" />

      <ManualPara>
        Navigate to <code>https://aip.mi6group.com</code>. The platform redirects you automatically
        to <code>/#/dashboards/executive</code> after authentication. If you land on a different
        role's dashboard, use the <strong>VIEW AS</strong> selector and select <strong>Executive</strong>.
      </ManualPara>

      <ManualPara>
        The sidebar navigation is organized into four sections: <strong>OPERATE</strong> (Overview,
        Leaderboard, Accounts, Pipeline), <strong>PLAN</strong> (Sales Plan, Plan vs Actual),
        <strong>REFERENCE</strong> (Opportunity DNA, Methodology, Manual), and
        <strong>ADMIN</strong> (Settings). The Mi6 Morning Brief at <code>/#/today</code> surfaces
        the three most important signals from the prior day. Open it before the dashboard for
        context before numbers.
      </ManualPara>

      {/* ── Chapter 4 — Dashboard ────────────────────────────────────────────── */}
      <ManualSectionHeading id="dashboard" num={4} title="Your dashboard: every tile" />

      <ManualPara>
        Route — <code>/#/dashboards/executive</code>. Open it every morning. Read it top to bottom
        in the order described — the order is intentional.
      </ManualPara>

      <ManualImage
        src={shot01}
        alt="Executive dashboard"
        caption="Live screen: /#/dashboards/executive"
      />

      <StepList
        steps={[
          <span><strong>Annual Objective — top left.</strong> The top-down annual revenue target. This is the number the company has committed to. Everything else on this page is measured against it.</span>,
          <span><strong>Booked YTD.</strong> Total closed-won revenue year to date. The progress bar shows percentage of annual objective. A delta line shows the attainment percentage and links to the pipeline detail view.</span>,
          <span><strong>Forecast.</strong> Closed-won YTD plus weighted open pipeline. The coverage multiple shows how many times the remaining annual objective is covered by open pipeline. Click to view the full forecast breakdown.</span>,
          <span><strong>Gap to Objective.</strong> The absolute dollar shortfall between annual objective and current forecast. When this is zero, the year is covered. When it is not, this is the number that drives the next pipeline-build conversation.</span>,
          <span><strong>CP10 Aggregates strip.</strong> Below the four KPI tiles, this strip shows the aggregate checkpoint health across all active opportunities — the percentage of deals with each checkpoint confirmed. Weak checkpoints here identify the methodology gap that limits the win rate.</span>,
          <span><strong>AE Scoreboard table.</strong> Every account executive's quota, closed revenue, weighted pipeline, forecast, pipeline coverage, and account count in one table. Sort by any column. Click a row to view that account executive's pipeline detail. Red coverage means below 5x; amber means below the required coverage multiple.</span>,
          <span><strong>Coverage Composition rail (right).</strong> A bar chart showing Forecast, Closed, and Gap in proportional units. Click a bar to drill into the matching pipeline view.</span>,
          <span><strong>Risk Signals rail (right).</strong> Three live signals: number of account executives missing a sales plan, number of account executives below 5x pipeline coverage, and the current gap to annual objective. These are the three conversations to have in every weekly executive check-in.</span>,
        ]}
      />

      {/* ── Chapter 5 — Pipeline Health ──────────────────────────────────────── */}
      <ManualSectionHeading id="pipeline" num={5} title="Pipeline health: reading the funnel" />

      <ManualPara>
        Route — <code>/#/pipeline</code>. Open this view weekly, not daily.
      </ManualPara>

      <ManualImage
        src={shot03}
        alt="Pipeline health — Coverage and motion"
        caption="Live screen: /#/pipeline — Coverage and motion"
      />

      <ManualTable
        headers={["Stage", "What it means"]}
        rows={[
          ["Suspect", "Early-stage awareness; the account executive has identified the account but no active conversation"],
          ["Prospect", "Initial conversation started; no defined project yet"],
          ["Discovery", "Active discovery; CP1 and CP2 being established"],
          ["Qualified Opp", "Project, budget context, and authority confirmed; CPs 1–4 green"],
          ["Proposal", "Solution and pricing presented; CPs 1–8 assessed"],
          ["Negotiation", "Terms and legal in motion; CPs 1–10 under review"],
        ]}
      />

      <ManualPara>
        A healthy funnel for manufacturing should have 3–5x of quarterly quota sitting in Qualified
        Opp or above. Deals sitting in Suspect or Prospect for more than 60 days without movement
        are pipeline inflation. Target coverage: 3.0x per quarter. The Aging Report on the pipeline
        page flags deals stuck more than 30 days in a stage — review every week.
      </ManualPara>

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        Real coverage vs inflated coverage
      </h3>

      <BulletList
        items={[
          <span><strong style={{ color: BLACK }}>Coverage is real when:</strong> COMMIT and UPSIDE deals have CP4 (Timeline) green with a firm timeline and a date. Average days in current stage is below historical average. The account executive has a named champion on every COMMIT deal.</span>,
          <span><strong style={{ color: BLACK }}>Coverage is inflated when:</strong> Deals have been in Discovery or Qualified Opp for 90+ days with no stage movement. COMMIT deals have CP4 blank or vague timeline. The pipeline count grew without a corresponding increase in Discovery calls or proposals.</span>,
        ]}
      />

      <ManualPara>
        The canonical forecast buckets are COMMIT, UPSIDE, OMITTED, and WON. COMMIT is the
        bucket for deals the account executive has committed to close this quarter. UPSIDE is
        possible deals that are not fully committed. OMITTED are deals excluded from the current
        forecast. WON is closed revenue. Read COMMIT as the defensible number. Never present UPSIDE
        to the board as committed revenue.
      </ManualPara>

      {/* ── Chapter 6 — Strategic Accounts ───────────────────────────────────── */}
      <ManualSectionHeading id="strategic" num={6} title="The strategic-account view: Tier 1 OEM portfolio" />

      <ManualPara>
        Route — <code>/#/accounts</code>. This is the CEO-attention surface.
      </ManualPara>

      <ManualImage
        src={shot05}
        alt="Strategic accounts — portfolio view"
        caption="Live screen: /#/accounts — portfolio view"
      />

      <ManualTable
        headers={["Account", "Industry", "Notes"]}
        rows={[
          ["Stellantis", "Automotive OEM", "Primary proof-case account; fully backfilled plan"],
          ["Toyota", "Automotive OEM", "Strategic anchor; long-cycle relationship"],
          ["Aptiv", "Tier 1 Supplier", "Active engagement; electrification context"],
          ["Marelli", "Tier 1 Supplier", "Restructuring tailwind; budget authority confirmed"],
          ["Ford", "Automotive OEM", "Multi-division opportunity"],
          ["Bosch NA", "Tier 1 Supplier", "Multi-plant footprint"],
          ["Bose", "Tier 1 Supplier", "Acoustics and cabin intelligence context"],
          ["Lear", "Tier 1 Supplier", "Seating and electrical systems focus"],
          ["Rivian", "EV OEM", "Growth account; scaling manufacturing context"],
          ["BorgWarner", "Tier 1 Supplier", "Powertrain transformation context"],
        ]}
      />

      <ManualPara>
        These ten accounts are reviewed personally by the CEO in the monthly strategic-account
        review. Not through a manager. Not through a report. Personally.
      </ManualPara>

      <ManualPara>
        Use this rule: any Tier 1 OEM account with a composite score below 70 and an estimated ARR
        above $500K should have a CEO-level touchpoint within 30 days. This is not a sales call —
        it is a strategic relationship call, peer to peer. The account executive prepares the context
        via the account card. The CEO reads the card before the call. The outcome is logged back
        into the account notes.
      </ManualPara>

      {/* ── Chapter 7 — Top-10 Account Sales Plan Workbook ───────────────────── */}
      <ManualSectionHeading id="sales-plan" num={7} title="Top-10 Account Sales Plan workbook" />

      <ManualPara>
        Route — <code>/#/sales-plan</code>. This is the primary sales planning surface for the
        executive. It shows the Top-10 strategic accounts as a two-panel workbook: a left rail
        with the 10-account list and a right pane showing the full plan detail for the selected
        account.
      </ManualPara>

      <ManualImage
        src={shot06}
        alt="Top-10 Account Sales Plan workbook"
        caption="Live screen: /#/sales-plan — Top-10 Account Sales Plan workbook"
      />

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        Left rail — 10-account list
      </h3>

      <ManualPara>
        The left rail shows all 10 strategic accounts in rank order. Each account entry displays
        the account name and a posture pill indicating the current forecast position. Stellantis
        is fully populated as the primary proof case. The remaining nine accounts render with their
        baseline structure and are available for plan population. Click any account to load its
        plan detail in the right pane.
      </ManualPara>

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        Right pane — Plan Detail (seven sections)
      </h3>

      <StepList
        steps={[
          <span><strong>ICP Fit.</strong> Ideal Customer Profile alignment for this account — industry, size, manufacturing context, and the specific pain signals that make this account qualify as a Tier 1 target.</span>,
          <span><strong>Intel.</strong> Account intelligence — recent news, buying signals, organizational changes, and any Compelling Reason to Buy that has been surfaced.</span>,
          <span><strong>Relationship and Entry.</strong> Current relationship status — who is known, what the entry point is, and which champion has been identified or is being developed.</span>,
          <span><strong>Persona Triad.</strong> The three buyer personas that must be engaged for this account: the economic buyer, the technical evaluator, and the champion. Each is mapped to a named contact where one exists.</span>,
          <span><strong>Checkpoint Snapshot.</strong> A read-only view of the 10 M-PACT checkpoints (CP1–CP10) for the active opportunity at this account. Green means confirmed. Red means gap. The executive reads this to know where the deal is structurally weak.</span>,
          <span><strong>Cadence Bundle.</strong> The current outreach cadence active on this account — hooks in play, BDR touchpoints scheduled, and marketing campaign themes tied to this account's ABM theme.</span>,
        ]}
      />

      <Callout>
        <strong style={{ color: BLACK }}>Quota Workbook.</strong> The Quota Workbook — annual
        quota by account executive, by quarter, by territory — is at{" "}
        <code>/#/sales-plan/quota</code>. This tab is visible to managers and executives only.
        Use it to set and review quota distribution. The Top-10 workbook at{" "}
        <code>/#/sales-plan</code> and the Quota Workbook at{" "}
        <code>/#/sales-plan/quota</code> are two distinct surfaces on the same page.
      </Callout>

      {/* ── Chapter 8 — Forecast vs Plan ─────────────────────────────────────── */}
      <ManualSectionHeading id="forecast" num={8} title="Forecast vs plan: building the board narrative" />

      <ManualPara>
        Route — <code>/#/plan-vs-actual</code>. This is the boardroom preparation surface.
      </ManualPara>

      <ManualImage
        src={shot02}
        alt="Revenue rollup — Plan vs Actual Q2 2026"
        caption="Live screen: /#/plan-vs-actual — Q2 2026 period view"
      />

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        The four stat cards
      </h3>

      <StepList
        steps={[
          <span><strong>Overall Attainment</strong> — blended attainment across all account executives.</span>,
          <span><strong>Closed Quarter</strong> — dollars closed this quarter vs the quarterly target.</span>,
          <span><strong>Weighted Pipeline</strong> — the forward-looking coverage number.</span>,
          <span><strong>Gap to Plan</strong> — the shortfall in absolute dollars.</span>,
        ]}
      />

      <ManualPara>
        Read these four in sequence: attainment first (where are we?), closed amount second (what
        did we land?), weighted pipeline third (can we close the gap?), gap to plan fourth (what
        is the exposure?).
      </ManualPara>

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        The three period toggles
      </h3>

      <BulletList
        items={[
          <span><strong>Month</strong> — tactical read. Use in the final two weeks of a month.</span>,
          <span><strong>Quarter</strong> — your primary executive view. Use weekly from week 4 of a quarter onward.</span>,
          <span><strong>Year</strong> — the strategic read. Use at QBR and before board meetings. This is the number you defend.</span>,
        ]}
      />

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        What numbers to defend in the boardroom
      </h3>

      <ManualPara>
        The defensible number for the board is: WON closed revenue plus weighted COMMIT pipeline.
        Do not include UPSIDE in a board commitment. Before any board meeting, open
        <code> /#/plan-vs-actual</code>, set the toggle to Year, and read: year-to-date closed
        against the annual plan, the weighted pipeline in COMMIT for remaining quarters, and the
        gap (annual plan minus YTD closed plus weighted COMMIT).
      </ManualPara>

      <ManualPara>
        Present UPSIDE to the board as a range, not a commitment: "Our base case for the year is $X,
        based on WON plus weighted COMMIT. If the following three accounts close before year-end,
        we can reach $Y." Name the three accounts. The board wants specifics, not percentages.
      </ManualPara>

      {/* ── Chapter 9 — Coaching the Coaches ────────────────────────────────── */}
      <ManualSectionHeading id="coaching" num={9} title="Coaching the coaches: 1-on-1s with your VP and managers" />

      <ManualPara>
        The executive's coaching surface is the VP of Sales or the regional sales manager — not
        individual account executives. AIP gives you the data to run that 1-on-1 without requiring
        a deck from the manager.
      </ManualPara>

      <ManualImage
        src={shot04}
        alt="Team attainment leaderboard"
        caption="Live screen: /#/leaderboard — per-AE attainment"
      />

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        Before the 1-on-1: what to pull
      </h3>

      <StepList
        steps={[
          <span><strong><code>/#/leaderboard</code> — sort by attainment.</strong> Identify who is above plan, who is flat, and who is falling. Note the trend vs prior quarter column. This is the conversation anchor.</span>,
          <span><strong><code>/#/pipeline</code> — coverage by quarter.</strong> Is Q1 of next year being built now? If not, ask why.</span>,
          <span><strong><code>/#/plan-vs-actual</code> — Year view.</strong> What is the manager's team contributing to the annual number? Is it on track?</span>,
        ]}
      />

      <ManualPara>
        Bring these three screens into the 1-on-1. Do not send screenshots — open the live data
        during the conversation. The data is real-time; printed screenshots go stale.
      </ManualPara>

      <ManualTable
        headers={["Condition", "Action"]}
        rows={[
          ["Account executive at below 60% attainment for two consecutive quarters", "Formal performance review"],
          ["Team coverage below 2x for the current quarter with 30 days remaining", "Emergency pipeline build sprint"],
          ["Tier 1 OEM account stuck in same stage for 60+ days", "CEO-level account touchpoint"],
          ["Win rate below 35% for trailing 90 days", "Methodology review with Bryan K. Short, Founder and CEO"],
          ["COMMIT deal with CP5 blank entering final 30 days of quarter", "Deal review: the deal is not COMMIT"],
        ]}
      />

      {/* ── Chapter 10 — Rhythm ───────────────────────────────────────────────── */}
      <ManualSectionHeading id="rhythm" num={10} title="Monthly and quarterly rhythm: the executive operating cadence" />

      <h3 style={{ color: BLACK, fontSize: 16, fontWeight: 700, marginTop: 12, marginBottom: 6 }}>
        Daily — two minutes
      </h3>
      <StepList
        steps={[
          <span>Open <code>/#/dashboards/executive</code>.</span>,
          <span>Read Annual Objective vs Booked YTD — is the gap shrinking?</span>,
          <span>Read Gap to Objective.</span>,
          <span>Scan the Risk Signals rail for any new pipeline coverage or plan alerts.</span>,
        ]}
      />

      <h3 style={{ color: BLACK, fontSize: 16, fontWeight: 700, marginTop: 16, marginBottom: 6 }}>
        Weekly — fifteen minutes
      </h3>
      <BulletList
        items={[
          <span><strong>Monday.</strong> Open <code>/#/pipeline</code>. Review current quarter's coverage multiple and the aging report.</span>,
          <span><strong>Wednesday.</strong> Open <code>/#/leaderboard</code>. Flag any account executive that moved from yellow to orange since Monday.</span>,
          <span><strong>Friday.</strong> Open <code>/#/plan-vs-actual</code> in Quarter view. Read the gap to plan — is it shrinking, holding, or growing?</span>,
        ]}
      />

      <h3 style={{ color: BLACK, fontSize: 16, fontWeight: 700, marginTop: 16, marginBottom: 6 }}>
        Monthly — one hour
      </h3>
      <StepList
        steps={[
          <span><code>/#/plan-vs-actual</code> in Year view — year-to-date closed, weighted pipeline, annual gap. This is the monthly board narrative draft.</span>,
          <span><code>/#/accounts</code> filtered to Tier 1 OEMs — review checkpoint health. Identify any account with composite below 70 and plan a touchpoint.</span>,
          <span><code>/#/leaderboard</code> sorted by trend — who improved month over month, who declined.</span>,
          <span><code>/#/sales-plan</code> — review the Top-10 workbook. Confirm Stellantis and the next two highest-priority accounts have current checkpoint snapshots and cadence bundles active.</span>,
        ]}
      />

      <ManualPara>
        Outputs from the monthly review: one paragraph for the management team on where the year
        stands; a list of 1–3 specific manager coaching actions; a decision on whether any Tier 1
        OEM requires CEO-level intervention.
      </ManualPara>

      <h3 style={{ color: BLACK, fontSize: 16, fontWeight: 700, marginTop: 16, marginBottom: 6 }}>
        Quarterly — half day (QBR)
      </h3>

      <ManualPara>
        The quarterly business review (QBR) is the primary planning event. AIP prepares the data.
        The executive reads the platform before the QBR — not during. Walking into a QBR and opening
        the platform live for the first time in the meeting is a signal you are not operating the
        business.
      </ManualPara>

      <ManualPara>
        <strong>QBR preparation sequence (two days before):</strong> open Plan vs Actual in Year
        view and write down the annual number, YTD actual, weighted pipeline, and gap. Open
        Pipeline and note quarterly coverage multiples — flag any quarter below 3x. Open Leaderboard
        and note the bottom two account executives by attainment and trend. Open Accounts and review
        Tier 1 OEM composite scores. Open the Top-10 Sales Plan workbook and confirm each account
        has a current checkpoint snapshot. Review win-rate trend.
      </ManualPara>

      <ManualPara>
        <strong>QBR agenda:</strong> Year-to-date actuals vs plan (10 min). Quarter-close postmortem
        — what committed and did not close, why (15 min). Next quarter pipeline coverage review (20
        min). Strategic account health — Tier 1 OEM walkthrough (15 min). Win rate, days to close,
        and methodology health (10 min). Annual plan reforecast (20 min).
      </ManualPara>

      {/* ── Chapter 11 — Glossary ────────────────────────────────────────────── */}
      <ManualSectionHeading id="glossary" num={11} title="Glossary, gotchas, and closing note" />

      <BulletList
        items={[
          <span><strong style={{ color: BLACK }}>M-PACT</strong> — the Mi6 operating model. M = Methodology. P = Process / Prioritization. A = Accountability. C = Cadence / Coaching. T = Timing / Timeline management. Always hyphenated. Never written as "MPACT" or "mpact."</span>,
          <span><strong style={{ color: BLACK }}>ARR</strong> — Annual Recurring Revenue. The annualized value of committed contracts.</span>,
          <span><strong style={{ color: BLACK }}>COMMIT</strong> — the forecast bucket for deals the account executive commits to close this quarter. The most defensible forward-looking number in the forecast.</span>,
          <span><strong style={{ color: BLACK }}>UPSIDE</strong> — possible deals, not committed. Never present to the board as a committed number.</span>,
          <span><strong style={{ color: BLACK }}>OMITTED</strong> — deals excluded from the active forecast. They remain in the pipeline but carry zero forecast weight.</span>,
          <span><strong style={{ color: BLACK }}>WON</strong> — closed-won revenue. The only number that is fully real.</span>,
          <span><strong style={{ color: BLACK }}>Compelling Reason to Buy</strong> — the buyer-confirmed trigger that makes this purchase necessary now. Not a vendor-asserted benefit — a buyer-confirmed consequence of inaction. Lives under CP2 Compelling Pain.</span>,
          <span><strong style={{ color: BLACK }}>Firm / formal timeline</strong> — the buyer-stated date a project must close by, with a real consequence if it slips. Lives under CP4 Timeline. A deal without a confirmed firm timeline belongs in UPSIDE, not COMMIT.</span>,
          <span><strong style={{ color: BLACK }}>Win Rate</strong> — percentage of qualified opportunities (Qualified Opp stage or above) that closed won, trailing 90 days. Target: 40–55% in manufacturing B2B.</span>,
          <span><strong style={{ color: BLACK }}>Coverage Multiple</strong> — total weighted pipeline divided by quarterly quota target. Target: 3.0x.</span>,
          <span><strong style={{ color: BLACK }}>QBR</strong> — Quarterly Business Review. The executive-led review that reforecasts the year and identifies coaching priorities.</span>,
          <span><strong style={{ color: BLACK }}>Tier 1 OEM</strong> — the large automotive and industrial OEM accounts that anchor the manufacturing portfolio.</span>,
          <span><strong style={{ color: BLACK }}>Opportunity DNA / Helix</strong> — the AIP visual decision system. <code>/#/opportunity-dna</code> is the per-account diagnostic that maps where a deal sits across the 10 checkpoints. <code>/#/helix</code> is the territory-level aggregate view of checkpoint health across the full pipeline. The executive reads the per-account diagnostic through the account card composite score at <code>/#/opportunity-dna</code>.</span>,
          <span><strong style={{ color: BLACK }}>Top-10 Sales Plan workbook</strong> — the account-level sales plan at <code>/#/sales-plan</code>, showing the 10 strategic accounts with ICP Fit, Intel, Relationship and Entry, Persona Triad, Checkpoint Snapshot, and Cadence Bundle for each.</span>,
          <span><strong style={{ color: BLACK }}>Quota Workbook</strong> — the annual quota-by-account-executive plan at <code>/#/sales-plan/quota</code>. Visible to managers and executives only.</span>,
        ]}
      />

      <Callout tone="red">
        <strong style={{ color: BLACK }}>Common gotchas.</strong> The coverage multiple is not a proxy for attainment — a 32x coverage with 45-day close cycles means stale pipeline. COMMIT without CP5 is not COMMIT — enforce this in every QBR. Win rate below 35% is a methodology problem, not a market problem. The annual plan is set once — reforecasts are allowed at quarter close, not mid-quarter. The board number is WON closed revenue plus weighted COMMIT, not total pipeline. <code>/#/sales-plan</code> is the Top-10 Account workbook. The Quota Workbook is at <code>/#/sales-plan/quota</code>.
      </Callout>

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        Closing note
      </h3>
      <ManualPara>
        The executive who knows the number is not the executive who checks the number most often.
        It is the executive who built the system that makes the number knowable — by every manager,
        every account executive, and every coach in the organization. M-PACT is that system. AIP
        makes it visible.
      </ManualPara>

      <Callout>
        <strong style={{ color: BLACK }}>Closing note.</strong> The helix is the diagnostic.
        The path is the play.
      </Callout>
    </ManualLayout>
  );
}
